// Entity ID / display-name helpers shared by lib/utils.js and lib/links.js.
// Split out from utils.js to break a mutual import cycle: utils.js needs
// links.js's generateExternalLinks, and links.js needed these 4 exports from
// utils.js. Keeping them in a third, dependency-free module lets both import
// downward instead of from each other.
import { DEV } from "./configMgr";

export const RE_CATEGORY_PREFIXED_ID = /^(?!https?:)[a-z][-_a-z0-9]+:[a-z][-a-z]+:.*$/;

export const getCategoryKey = (catSrcID) => {
  return RE_CATEGORY_PREFIXED_ID.test(catSrcID) && catSrcID.split(':')[1];
};

export const getExternalID = id => {
  return (RE_CATEGORY_PREFIXED_ID.test(id) && id.slice(id.indexOf(':') + 1)) || id;
}

const PREFERRED_ALIAS_FORMATS = [
  // Single words, no digits
  /^[^\d\W]+$/,
  // with dashes (no inchi)
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|-)+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|-)+$/,
  // with spaces or commas
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|[-\s,])+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|[-\s,])+$/,
  // with parens/brackets/plus
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[^\d\W]([^\d\W]|[-+\s,()\][])+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)([^\d\W]|[-+\s,()\][])+$/,
  // with numbers
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)\D[-\w+\s,()\][]+$/,
  /^(?![A-Z]{14}-[A-Z]{10}-[A-Z]|S?CHEMBL|CID|UCI)[-\w+\s,()\][]+$/,
  // preferred datasets if no other matches
  // /^(CHEMBL\d+)$/,
  /^(CHEMBL|CID)\d+$/,
  // anything else
  /^(.*)$/,
]

const countNonwordChars = s => {
  return s.replace(/([^\d\W]|[-\s])/g, "").length;
};

const countDigits = s => {
  return s.replace(/\D/g, "").length;
};

export const getBestName = (entity, short = false) => {
  if (!entity) {
    return "<null>";
  }
  const noTitle = DEV ? " <no title>" : "";
  if (entity.entity) {
    entity = entity.entity;
  }
  if (entity.label) {
    if (short && entity.category === 'target' && entity.gene_symbol) {
      return entity.gene_symbol;
    }
    if (short && entity.aliases?.length > 0) {
      // Only prefer a short alias if the label looks like a systematic/IUPAC chemical name
      // (long, containing digits or brackets typical of chemical nomenclature)
      const isIupacLike = entity.label.length > 20 && /[\d\[\(]/.test(entity.label);
      if (isIupacLike) {
        const shortest = entity.aliases
          .filter(a => a.length > 3)
          .reduce((a, b) => a.length <= b.length ? a : b, entity.label);
        if (shortest.length < entity.label.length) {
          return shortest;
        }
      }
    }
    return entity.label;
  }
  if (entity.title) {
    return entity.title;
  }
  if (entity.category === 'target' && entity.gene_symbol) {
    return `${entity.gene_symbol}${noTitle}`;
  }
  const aliases = (entity.aliases || []).slice();
  if (aliases.length > 0) {
    if (short) {
      return aliases.reduce((a, b) => a.length <= b.length ? a : b);
    }
    for (let fmt of PREFERRED_ALIAS_FORMATS) {
      const candidates = []
      aliases.forEach(alias => {
        if (alias.match(fmt)) {
          candidates.push(alias);
        }
      });
      if (candidates.length > 0) {
        const sorted = candidates.sort((a, b) => {
          // Prefer CHEMBL > CID > SCHEMBL/BDB/HMDB > UCI
          if (/^(CHEMBL|CID)/i.test(a) && /^(CHEMBL|CID)/i.test(b)) {
            return a - b;
          }
          const delta = countNonwordChars(a) - countNonwordChars(b);
          const delta2 = countDigits(a) - countDigits(b);
          return delta + delta2;
        });
        return `${sorted[0]}${noTitle}`;
      }
    }
  }
  if (entity.iupac) {
    return `${entity.iupac}${noTitle}`;
  }
  return null;
};
